import { useEffect, useState } from 'react';
import './PlanSubmissionForm.css';
import FileUploadZone from '../common/FileUploadZone';
import { getChefEquipes } from '../../api/teacher';
import { getSessions } from '../../api/sessions';
import { getProgrammes } from '../../api/programmes';
import { useAuth } from '../../auth/AuthContext';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const COURSE_NUMBER_PATTERN = /^\d{3}-[A-Za-z0-9]{3}-[A-Za-z0-9]{2}$/;
const ALLOWED_FILE_TYPES = new Set([
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
]);
const ALLOWED_EXTENSIONS = ['.pdf', '.doc', '.docx'];

const CAMPUS = [
  'Campus de Montréal',
  'Campus de Brossard'
];

export default function PlanSubmissionForm({ onSubmit, hideChefSelection = false }) {
  const { user } = useAuth();
  const [chefs, setChefs] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [programmes, setProgrammes] = useState([]);
  const [form, setForm] = useState({
    classeNom: '',
    chefId: '',
    session: '',
    numeroCours: '',
    programme: '',
    modeEnseignement: '',
    campus: '',
  });
  const [file, setFile] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Check if user is a chef d'équipe - use prop or try to detect from user role
  const isChef = hideChefSelection || user?.role === 'CE' || user?.role?.toLowerCase().includes('chef');

  useEffect(() => {
    const loadChefs = async () => {
      const data = await getChefEquipes();
      setChefs(data);
    };
    loadChefs();
  }, []);

  useEffect(() => {
    const loadSessions = async () => {
      try {
        const data = await getSessions(true); // Only get active sessions
        setSessions(data);
      } catch (err) {
        console.error('Erreur chargement sessions:', err);
        setSessions([]);
      }
    };
    loadSessions();
  }, []);

  useEffect(() => {
    const loadProgrammes = async () => {
      try {
        const data = await getProgrammes(true); // Only get active programmes
        setProgrammes(data);
      } catch (err) {
        console.error('Erreur chargement programmes:', err);
        setProgrammes([]);
      }
    };
    loadProgrammes();
  }, []);

  const handleChange = (event) => {
    const { name, value } = event.target;
    const finalValue = name === 'numeroCours' ? value.toUpperCase() : value;
    setForm((prev) => ({ ...prev, [name]: finalValue }));
  };

  const courseNumberValid = form.numeroCours.trim() === '' || COURSE_NUMBER_PATTERN.test(form.numeroCours.trim());

  const hasAllowedExtension = (filename = '') => {
    const lowerName = filename.toLowerCase();
    return ALLOWED_EXTENSIONS.some((ext) => lowerName.endsWith(ext));
  };

  const handleFile = (selected) => {
    setError('');
    if (!selected) {
      setFile(null);
      return;
    }
    if (!ALLOWED_FILE_TYPES.has(selected.type) && !hasAllowedExtension(selected.name)) {
      setError('Le fichier doit être au format PDF, DOC ou DOCX.');
      return;
    }
    if (selected.size > MAX_FILE_SIZE) {
      setError('Le fichier dépasse 10 Mo.');
      return;
    }
    setFile(selected);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    if (!file) {
      setError('Veuillez sélectionner un fichier PDF, DOC ou DOCX.');
      return;
    }

    // Chef selection is required only for teachers (not for chefs submitting their own plans)
    if (!isChef && !form.chefId) {
      setError('Veuillez sélectionner un chef d\'équipe.');
      return;
    }

    if (!COURSE_NUMBER_PATTERN.test(form.numeroCours.trim())) {
      setError('Le numéro du cours doit respecter le format 109-101-MQ.');
      return;
    }

    setLoading(true);
    try {
      const payload = new FormData();
      // Only add chef_id if user is not a chef (chefs don't need a chef assigned)
      if (!isChef && form.chefId) {
        payload.append('chef_id', form.chefId);
      }
      payload.append('classe_nom', form.classeNom);
      payload.append('file', file);
      payload.append('session', form.session);
      payload.append('numero_cours', form.numeroCours.trim().toUpperCase());
      payload.append('programme', form.programme);
      payload.append('mode_enseignement', form.modeEnseignement);
      payload.append('campus', form.campus);

      await onSubmit(payload);

      setForm({ 
        classeNom: '', 
        chefId: '',
        session: '',
        numeroCours: '',
        programme: '',
        modeEnseignement: '',
        campus: '',
      });
      setFile(null);
    } catch (err) {
      setError(err.message || 'Erreur lors du téléversement');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form className="plan-form" onSubmit={handleSubmit}>
      <div className="form-header">
        <div>
          <h3>Soumettre un nouveau plan</h3>
          <p>Glissez votre fichier PDF/Word et complétez les informations.</p>
        </div>
      </div>

      {error && <div className="form-error">{error}</div>}

      <div className="form-section">
        <h4>Télécharger le fichier (PDF, DOC, DOCX)</h4>
        <FileUploadZone file={file} onFileSelect={handleFile} />
      </div>

      <div className="form-section">
        <h4>Informations sur le plan de cours</h4>
        {isChef && (
          <div style={{
            padding: '12px',
            marginBottom: '16px',
            backgroundColor: '#e3f2fd',
            border: '1px solid #2196f3',
            borderRadius: '8px',
            fontSize: '14px',
            color: '#1565c0'
          }}>
            ℹ️ En tant que chef d'équipe, votre plan sera validé directement par le conseiller pédagogique.
          </div>
        )}
        <div className="form-grid">
          {!isChef && (
            <label>
              Chef d'équipe
              <select name="chefId" value={form.chefId} onChange={handleChange} required>
                <option value="">Sélectionner un chef d'équipe</option>
                {chefs.map((chef) => (
                  <option key={chef.id} value={chef.id}>
                    {chef.nom}
                  </option>
                ))}
              </select>
            </label>
          )}

          <label>
            Titre du cours
            <input
              type="text"
              name="classeNom"
              value={form.classeNom}
              onChange={handleChange}
              placeholder="Ex. Activité physique et santé"
              required
            />
          </label>

          <label>
            Session
            <select name="session" value={form.session} onChange={handleChange}>
              <option value="">Sélectionner une session</option>
                {sessions.map((session) => (
                  <option key={session.id} value={session.code}>
                    {session.nom} ({session.code})
                </option>
              ))}
            </select>
          </label>

          <label>
            Numéro du cours
            <input
              type="text"
              name="numeroCours"
              value={form.numeroCours}
              onChange={handleChange}
              placeholder="Ex. 109-101-MQ"
              pattern="\d{3}-[A-Za-z0-9]{3}-[A-Za-z0-9]{2}"
              title="Format requis: 109-101-MQ (ex: 420-TT-MQ)"
              required
              style={form.numeroCours.trim() && !courseNumberValid ? { borderColor: '#ef4444', outline: 'none' } : {}}
            />
            {form.numeroCours.trim() && !courseNumberValid && (
              <span style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                Format invalide. Attendu: 3 chiffres-3 caractères-2 caractères (ex: 109-101-MQ)
              </span>
            )}
            {form.numeroCours.trim() && courseNumberValid && (
              <span style={{ color: '#16a34a', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                ✓ Format valide
              </span>
            )}
          </label>

          <label>
            Programme
            <select name="programme" value={form.programme} onChange={handleChange}>
              <option value="">Sélectionner un programme</option>
              {programmes.map((prog) => (
                <option key={prog.id} value={prog.nom}>
                  {prog.nom}
                </option>
              ))}
            </select>
          </label>

          <label>
            Mode d'enseignement
            <select name="modeEnseignement" value={form.modeEnseignement} onChange={handleChange}>
              <option value="">Sélectionner un mode</option>
              <option value="Présentiel">Présentiel</option>
              <option value="Hybride">Hybride</option>
              <option value="Distance">Distance</option>
            </select>
          </label>

          <label>
            Campus
            <select name="campus" value={form.campus} onChange={handleChange}>
              <option value="">Sélectionner un campus</option>
              {CAMPUS.map((campus) => (
                <option key={campus} value={campus}>
                  {campus}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="form-actions">
          <button type="submit" disabled={loading}>
            {loading ? 'Téléversement...' : 'Soumettre pour révision'}
          </button>
        </div>
      </div>

      <div className="form-section">
        <h4>Aperçu de la liste de vérification</h4>
        <ul className="checklist">
          <li>☑️ Les objectifs d'apprentissage sont clairement définis.</li>
          <li>☑️ Le contenu du cours est pertinent et à jour.</li>
          <li>☑️ Les méthodes d'évaluation sont appropriées.</li>
          <li>☑️ Les références bibliographiques sont complètes.</li>
          <li>☑️ Le plan de cours est cohérent dans son ensemble.</li>
        </ul>
      </div>
    </form>
  );
}
